import { ButtonLoading } from "@/components/button-loading";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useIdempotencyKey } from "@/hooks/use-idempotency-key";
import { useLicenseErrorToast } from "@/hooks/use-license-error-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { formatCurrency as formatCurrencyIntl } from "@/lib/format-amount";
import {
  FrequencySwitchPreviewDTO,
  TeamLicenseInventoryDTO,
} from "@shaarp/common";
import { InfoIcon } from "lucide-react";
import { useEffect, useState } from "react";

type DialogFrequencySwitchProps = {
  fromFrequency: "monthly" | "annual";
  toFrequency: "monthly" | "annual";
  onClose: () => void;
  onConfirmed: (updatedInventory: TeamLicenseInventoryDTO) => void;
};

/**
 * Dialog for switching billing frequency (monthly ↔ annual).
 * Fetches a preview of the cost impact and requires explicit confirmation.
 */
export const DialogFrequencySwitch = ({
  fromFrequency,
  toFrequency,
  onClose,
  onConfirmed,
}: DialogFrequencySwitchProps) => {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const showLicenseError = useLicenseErrorToast();
  const idempotencyKey = useIdempotencyKey();

  const [preview, setPreview] = useState<FrequencySwitchPreviewDTO | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fromLabel =
    fromFrequency === "monthly"
      ? t("team.licenseManagement.monthly")
      : t("team.licenseManagement.annual");

  const toLabel =
    toFrequency === "monthly"
      ? t("team.licenseManagement.monthly")
      : t("team.licenseManagement.annual");

  const formatCurrency = (amount: number, currency: string) =>
    formatCurrencyIntl(amount, currency, i18n.language, {
      minimumFractionDigits: 2,
    });

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        setIsLoadingPreview(true);
        const { data } = await api.get<FrequencySwitchPreviewDTO>(
          `/my/team/licenses/frequency-switch/preview?from=${fromFrequency}&to=${toFrequency}`
        );
        setPreview(data);
      } catch {
        toastError();
        onClose();
      } finally {
        setIsLoadingPreview(false);
      }
    };
    fetchPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromFrequency, toFrequency]);

  const handleConfirm = async () => {
    if (!confirmed) return;
    try {
      setIsSubmitting(true);
      const { data } = await api.post<TeamLicenseInventoryDTO>(
        "/my/team/licenses/frequency-switch",
        {
          from: fromFrequency,
          to: toFrequency,
          confirmed: true,
        },
        { headers: { "Idempotency-Key": idempotencyKey } }
      );
      toast({
        variant: "default",
        title: t("team.licenseManagement.frequencySwitchDialog.title", {
          toFrequency: toLabel,
        }),
        description: t("team.licenseManagement.frequencySwitchDialog.confirm"),
      });
      onConfirmed(data);
    } catch (error) {
      showLicenseError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[480px] w-full">
        <DialogHeader>
          <DialogTitle>
            {t("team.licenseManagement.frequencySwitchDialog.title", {
              toFrequency: toLabel,
            })}
          </DialogTitle>
        </DialogHeader>

        <Separator />

        {isLoadingPreview ? (
          <div className="space-y-3">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-5 w-1/2" />
          </div>
        ) : preview ? (
          <div className="space-y-4">
            {/* Cost breakdown */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("team.licenseManagement.frequencySwitchDialog.currentCost", {
                    frequency: fromLabel,
                  })}
                </span>
                <span className="font-medium">
                  {formatCurrency(preview.currentRecurringCost, preview.currency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("team.licenseManagement.frequencySwitchDialog.newCost", {
                    frequency: toLabel,
                  })}
                </span>
                <span className="font-medium">
                  {formatCurrency(preview.newRecurringCost, preview.currency)}
                </span>
              </div>
              {preview.estimatedYearlySavings > 0 && (
                <div className="flex justify-between text-green-600 dark:text-green-400">
                  <span>
                    {t("team.licenseManagement.frequencySwitchDialog.yearlySavings")}
                  </span>
                  <span className="font-semibold">
                    {formatCurrency(preview.estimatedYearlySavings, preview.currency)}
                  </span>
                </div>
              )}
            </div>

            <Separator />

            {/* Immediate charge */}
            <div className="flex justify-between text-sm font-medium">
              <span>
                {t("team.licenseManagement.frequencySwitchDialog.immediateCharge")}
              </span>
              <span>
                {formatCurrency(
                  preview.estimatedImmediateChargeExclTax,
                  preview.currency
                )}
              </span>
            </div>

            {/* Migration warning */}
            <Alert>
              <InfoIcon className="w-4 h-4" />
              <AlertDescription className="text-xs">
                {t("team.licenseManagement.frequencySwitchDialog.migrationNote", {
                  fromFrequency: fromLabel,
                  toFrequency: toLabel,
                })}
              </AlertDescription>
            </Alert>

            {/* Confirmation checkbox */}
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={confirmed}
                onCheckedChange={(checked) => setConfirmed(Boolean(checked))}
                className="mt-0.5"
              />
              <span className="text-sm">
                {t("team.licenseManagement.frequencySwitchDialog.confirmCheckbox")}
              </span>
            </label>
          </div>
        ) : null}

        <Separator />

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {t("team.licenseManagement.frequencySwitchDialog.back")}
          </Button>
          <ButtonLoading
            isLoading={isSubmitting}
            disabled={!confirmed || isSubmitting || isLoadingPreview}
            onClick={handleConfirm}
          >
            {t("team.licenseManagement.frequencySwitchDialog.confirm")}
          </ButtonLoading>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
